import { AlertTriangle, RefreshCw, Inbox } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Unified load/error/empty banner for data panels.
 *
 * Renders exactly one of:
 *   - loading banner  (status === "loading")
 *   - error banner    (status === "error")  with HTTP status + detail + Retry
 *   - empty banner    (status === "ok" && isEmpty)
 *   - null            (status === "ok" && !isEmpty — caller renders content)
 *
 * The banner distinguishes "the request failed" from "there is genuinely no data",
 * so a silent empty grid never masks an auth expiry, permission error, or backend
 * fault. Same pattern used across every list panel in the app.
 */
export default function PanelState({
  status,          // "loading" | "error" | "ok"
  error,           // { status: number|null, message: string } | null
  isEmpty = false, // caller decides what "empty" means for its data
  onRetry,         // () => void
  emptyTitle = "Nothing here yet",
  emptyBody = null,
  variant = "card", // "card" (default, boxed) | "inline" (compact, for embedded use)
  testIdPrefix = "panel",
}) {
  const nav = useNavigate();

  if (status === "loading") {
    return (
      <div
        data-testid={`${testIdPrefix}-loading`}
        className={
          variant === "inline"
            ? "flex items-center gap-2 text-[12.5px] text-[color:var(--cc-ink-muted)] py-2"
            : "bg-white border border-[#e7e9ee] rounded-xl p-6 flex items-center gap-3 text-[13px] text-[color:var(--cc-ink-muted)]"
        }
      >
        <RefreshCw size={variant === "inline" ? 13 : 16} strokeWidth={1.8} className="animate-spin text-[#0072bc]" />
        Loading…
      </div>
    );
  }

  if (status === "error") {
    const httpStatus = error?.status;
    return (
      <div
        data-testid={`${testIdPrefix}-error`}
        className="bg-[#fff5f5] border border-[#f5c2c2] rounded-xl p-5"
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 text-[#b3261e]">
            <AlertTriangle size={20} strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-[15px] text-[#7a1c1c]">
              Couldn’t load data
            </div>
            <div className="text-[12.5px] text-[#7a1c1c]/85 mt-1">
              {httpStatus === 401 && (
                <>Your session is expired or missing. Sign in again, then reload this page.</>
              )}
              {httpStatus === 403 && (
                <>Your account doesn’t have permission to view this data.</>
              )}
              {httpStatus && ![401, 403].includes(httpStatus) && (
                <>The server returned an error while loading this panel.</>
              )}
              {!httpStatus && (
                <>Couldn’t reach the API. Check your network or the backend service.</>
              )}
            </div>
            <div className="mt-2 text-[11.5px] font-mono-data text-[#7a1c1c]/80 break-all">
              <span className="uppercase tracking-widest mr-2">Status</span>
              <span data-testid={`${testIdPrefix}-error-status`}>{httpStatus ?? "network"}</span>
              <span className="mx-2 opacity-60">·</span>
              <span data-testid={`${testIdPrefix}-error-detail`}>{error?.message || "unknown error"}</span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  data-testid={`${testIdPrefix}-retry`}
                  className="text-[12px] font-semibold px-3 py-1.5 rounded-md border border-[#f5c2c2] bg-white text-[#7a1c1c] hover:bg-[#fff0f0] inline-flex items-center gap-1.5"
                >
                  <RefreshCw size={13} strokeWidth={1.8} /> Retry
                </button>
              )}
              {httpStatus === 401 && (
                <button
                  type="button"
                  onClick={() => nav("/login")}
                  data-testid={`${testIdPrefix}-signin`}
                  className="text-[12px] font-semibold px-3 py-1.5 rounded-md bg-[#7a1c1c] text-white hover:bg-[#5f1414]"
                >
                  Sign in
                </button>
              )}
            </div>
            <div className="mt-3 text-[11px] text-[#7a1c1c]/70">
              This message means the request failed — your data on the server is unaffected.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === "ok" && isEmpty) {
    return (
      <div
        data-testid={`${testIdPrefix}-empty`}
        className="bg-white border border-dashed border-[#e7e9ee] rounded-xl p-8 text-center"
      >
        <div className="mx-auto w-10 h-10 rounded-full bg-[#eef4fb] text-[#0072bc] flex items-center justify-center mb-3">
          <Inbox size={20} strokeWidth={1.8} />
        </div>
        <div className="font-display font-semibold text-[15px] text-[#0e1420]">
          {emptyTitle}
        </div>
        {emptyBody && (
          <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] mt-1">{emptyBody}</div>
        )}
      </div>
    );
  }

  return null;
}

/**
 * Convenience helper — extracts a normalized { status, message } from an axios error.
 * Kept in this file so panels can `import { extractApiError }` alongside <PanelState>.
 */
export function extractApiError(err) {
  const status = err?.response?.status ?? null;
  const detail = err?.response?.data?.detail;
  let message;
  if (typeof detail === "string") message = detail;
  else if (Array.isArray(detail)) message = detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  else if (detail && typeof detail.msg === "string") message = detail.msg;
  else message = null;
  if (!message) message = err?.message || (status ? "Server error" : "Network error");
  return { status, message };
}
